import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { EventsStatCard } from './events-stat-card';
import { EventsAlertCard } from './events-alert-card';
import { EventsNodeRow } from './events-node-row';
import { EntryRow } from './entry-row';
import { useEnteringRows } from './helpers/use-entering-rows';
import type { WorkflowSummaryStats } from '@/hooks/use-events-summary';

interface EventsSummaryProps {
  summary: WorkflowSummaryStats;
  onSelectNode: (nodeId: string) => void;
  isExecuting?: boolean;
}

export function EventsSummary({
  summary,
  onSelectNode,
  isExecuting = false,
}: EventsSummaryProps) {
  const hasAlerts =
    summary.modelFailures.length > 0 || summary.retries.length > 0;

  const orderedNodes = useMemo(
    () => [...summary.nodes].reverse(),
    [summary.nodes],
  );
  const nodeKeys = useMemo(
    () => orderedNodes.map((node) => node.nodeId),
    [orderedNodes],
  );
  const enteringNodes = useEnteringRows(nodeKeys);

  const showProgress = summary.isRunning || isExecuting;

  // Run dispatched, no events yet: show a starting state instead of "0 events".
  const isInitializing = isExecuting && summary.totalEvents === 0;

  const progressPercent =
    summary.totalNodes > 0
      ? Math.round((summary.completedNodes / summary.totalNodes) * 100)
      : 0;

  const totalModelFailures = summary.modelFailures.reduce(
    (sum, f) => sum + f.count,
    0,
  );
  const totalRetries = summary.retries.reduce((sum, r) => sum + r.count, 0);

  return (
    <div className='flex flex-col flex-1 overflow-y-auto'>
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center justify-between'>
          <h3 className='text-sm font-semibold'>Workflow Summary</h3>
          {isInitializing ? (
            <span className='inline-flex items-center gap-1.5 text-sm text-muted-foreground'>
              <Loader2 className='size-3 animate-spin' />
              Starting run
            </span>
          ) : (
            <span className='text-sm text-muted-foreground'>
              {summary.totalEvents}{' '}
              {summary.totalEvents === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>

        {isInitializing ? (
          <div
            className='relative h-1.5 w-full overflow-hidden rounded-full bg-primary/20'
            role='progressbar'
            aria-label='Starting workflow'
          >
            <div className='animate-progress-indeterminate absolute inset-y-0 left-0 w-1/3 rounded-full bg-primary' />
          </div>
        ) : (
          showProgress && (
            <div className='flex items-center gap-2'>
              <Progress
                value={progressPercent}
                shimmer
                className='h-1.5 flex-1'
              />
              <span className='text-sm text-muted-foreground tabular-nums'>
                {progressPercent}%
              </span>
            </div>
          )
        )}

        <div className='flex gap-2'>
          <EventsStatCard
            value={summary.totalThoughts}
            label='Thought'
            className={isInitializing ? 'opacity-60' : undefined}
          />
          <EventsStatCard
            value={summary.totalToolCalls}
            label='Tool call'
            className={isInitializing ? 'opacity-60' : undefined}
          />
          <EventsStatCard
            value={summary.totalOutputs}
            label='Output'
            className={isInitializing ? 'opacity-60' : undefined}
          />
        </div>

        {hasAlerts && (
          <div className='flex flex-col gap-2'>
            {totalModelFailures > 0 && (
              <EventsAlertCard
                variant='error'
                count={totalModelFailures}
                label={`Model failure${totalModelFailures > 1 ? 's' : ''}`}
                description={summary.modelFailures[0]?.description}
              />
            )}
            {totalRetries > 0 && (
              <EventsAlertCard
                variant='warning'
                count={totalRetries}
                label={`Retr${totalRetries > 1 ? 'ies' : 'y'}`}
                description={summary.retries[0]?.description}
              />
            )}
          </div>
        )}
      </div>

      {isInitializing && <StartingNodeSkeleton />}

      {!isInitializing && summary.nodes.length > 0 && (
        <>
          <Separator />
          <div className='flex flex-col'>
            {orderedNodes.map((node, index) => (
              <EntryRow
                key={node.nodeId}
                isEntering={enteringNodes.has(node.nodeId)}
                className='overflow-hidden'
              >
                <EventsNodeRow node={node} onClick={onSelectNode} />
                {index < orderedNodes.length - 1 && <Separator />}
              </EntryRow>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StartingNodeSkeleton() {
  return (
    <>
      <Separator />
      <div className='flex flex-col'>
        <div className='flex gap-3 px-4 py-3.5'>
          <Skeleton className='size-9 shrink-0 rounded-md' />
          <div className='flex flex-1 flex-col gap-2 pt-1'>
            <Skeleton className='h-2.5 w-24 rounded' />
            <Skeleton className='h-2 w-40 rounded' />
          </div>
        </div>
        <Separator />
        <div className='flex gap-3 px-4 py-3.5 opacity-60'>
          <Skeleton className='size-9 shrink-0 rounded-md' />
          <div className='flex flex-1 flex-col gap-2 pt-1'>
            <Skeleton className='h-2.5 w-20 rounded' />
            <Skeleton className='h-2 w-36 rounded' />
          </div>
        </div>
        <Separator />
        <p className='px-4 py-4 text-sm text-muted-foreground'>
          Waiting for the first event from the run.
        </p>
      </div>
    </>
  );
}
